import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { EXAMPLES, WEBSITE_EXAMPLES, startProject, formatError } from "@/lib/api";

const BOT_EXAMPLES = ["Bot de pedidos para restaurante", "Bot de ventas para tienda", "Bot de citas para consultorio", "Bot de atención para inmobiliaria", "Bot de reservas para hotel", "Bot de cotizaciones para taller"];
import { PageHeader, Card } from "@/pages/AppLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function NewApp() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState("app");
  const examples = type === "website" ? WEBSITE_EXAMPLES : type === "chatbot" ? BOT_EXAMPLES : EXAMPLES;

  const create = async () => {
    if (!prompt.trim()) return toast.error(type === "chatbot" ? "Describe qué debe hacer tu chatbot" : type === "website" ? "Describe la página web que quieres crear" : "Describe la aplicación que quieres crear");
    setBusy(true);
    try {
      const p = await startProject(prompt.trim(), name.trim() || undefined, type);
      navigate(`/app/proyecto/${p.id}`, { state: type === "chatbot" ? null : { initialPrompt: prompt.trim() } });
    } catch (e) {
      toast.error(formatError(e));
      setBusy(false);
    }
  };

  return (
    <div data-testid="new-app-page">
      <PageHeader eyebrow="Nuevo proyecto" title={type === "chatbot" ? "Crear chatbot de WhatsApp" : type === "website" ? "Crear página web" : "Crear nueva aplicación"} subtitle="Describe tu idea con tus propias palabras. Cuanto más detalle, mejor resultado." />
      <Card className="space-y-6 max-w-3xl">
        <div className="grid sm:grid-cols-3 gap-3" data-testid="project-type-selector">
          {[["app", "Aplicación", "Sistemas con datos: inventarios, ventas, citas, clientes…"], ["website", "Página web", "Sitios y landing pages con secciones, galería, SEO y formularios de contacto"], ["chatbot", "Chatbot WhatsApp", "Asistente con IA que atiende a tus clientes por WhatsApp"]].map(([v, l, d]) => (
            <button key={v} onClick={() => setType(v)} data-testid={`project-type-${v}`}
              className={`text-left rounded-xl border p-4 transition-colors ${type === v ? "border-cyan-400/60 bg-cyan-500/10" : "border-[#222B40] hover:border-blue-500/40"}`}>
              <p className="text-white font-medium">{l}</p><p className="text-xs text-slate-400 mt-1">{d}</p>
            </button>
          ))}
        </div>
        <div className="space-y-2">
          <label className="text-sm text-slate-300">Nombre del proyecto (opcional)</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Inventario Ferretería López" data-testid="new-app-name-input" className="bg-[#0A0D14]" />
        </div>
        <div className="space-y-2">
          <label className="text-sm text-slate-300">{type === "website" ? "¿Qué página web quieres crear?" : type === "chatbot" ? "¿Qué debe hacer tu chatbot?" : "¿Qué aplicación quieres crear hoy?"}</label>
          <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={6} data-testid="new-app-prompt-input" className="bg-[#0A0D14]"
            placeholder={type === "chatbot" ? "Ej: Un asistente para mi pastelería que responda precios, horarios y tome pedidos…" : type === "website" ? "Ej: Un sitio para mi restaurante con menú, galería, reservaciones y ubicación…" : "Ej: Una agenda de citas para mi consultorio, con pacientes, horarios disponibles y recordatorios…"} />
        </div>
        <div className="flex flex-wrap gap-2">
          {examples.map((ex, i) => (
            <button key={ex} onClick={() => { setPrompt(type === "website" ? `Quiero un ${ex.toLowerCase()} con inicio, servicios, galería, testimonios, preguntas frecuentes y formulario de contacto.` : type === "chatbot" ? `${ex} que responda dudas, precios y horarios, y tome pedidos.` : `Quiero un ${ex.toLowerCase()} para mi negocio.`); if (!name) setName(ex); }} data-testid={`new-app-chip-${i}`}
              className="px-3 py-1.5 rounded-full border border-[#222B40] text-slate-300 text-xs hover:border-cyan-400/60 hover:text-cyan-200 transition-colors">{ex}</button>
          ))}
        </div>
        <button onClick={create} disabled={busy} data-testid="new-app-create-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-400 disabled:opacity-60 text-white font-semibold transition-colors">
          <Sparkles className="w-4 h-4" />{busy ? "Creando…" : "Crear y abrir el constructor"}
        </button>
      </Card>
    </div>
  );
}
